import { CartLinesUpdateEvent } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';

class GooseCartAddons extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.addEventListener('submit', this.add, { signal });
    this.addEventListener('change', this.select, { signal });
    this.rank();
    this.observer = new MutationObserver(() => this.rank());
    this.observer.observe(this, { attributes: true, attributeFilter: ['data-remaining'], childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  rank = () => {
    const gap = Number(this.dataset.remaining);
    const cards = [...this.querySelectorAll('[data-goose-card]')];
    cards.sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
    const bridge = gap > 0 ? cards.find(card => Number(card.dataset.price) >= gap) : null;
    if (bridge) cards.unshift(...cards.splice(cards.indexOf(bridge), 1));
    const list = this.querySelector('.goose-cart-addons__list');
    cards.forEach((card, index) => {
      if (list.children[index] !== card) list.insertBefore(card, list.children[index] || null);
      card.hidden = index >= 3;
      card.querySelector('[data-goose-qualifies]').hidden = !(gap > 0 && Number(card.dataset.price) >= gap);
    });
  };

  select = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-goose-card]');
    if (!card || !option?.dataset.price) return;
    card.querySelector('[data-goose-price]').textContent = option.dataset.money;
    card.querySelector('[data-goose-qualifies]').hidden = !(Number(this.dataset.remaining) > 0 && Number(option.dataset.price) >= Number(this.dataset.remaining));
  };

  add = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-goose-form]')) return;
    event.preventDefault();
    if (this.busy || !form.reportValidity()) return;
    this.busy = true;
    const data = new FormData(form);
    const button = form.querySelector('button');
    const label = button.textContent;
    const error = this.querySelector('[data-goose-error]');
    error.hidden = true;
    const drawer = this.closest('cart-drawer-component');
    const controls = [...drawer.querySelectorAll('button:not(.theme-drawer__close-button), select, input[type="number"]')].filter(control => !control.disabled);
    controls.forEach(control => { control.disabled = true; });
    button.textContent = this.dataset.adding;
    form.setAttribute('aria-busy', 'true');
    let added = false;
    try {
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
      const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(item => item.dataset.sectionId))];
      data.set('sections', sectionIds.slice(0, 5).join(','));
      data.set('sections_url', window.location.pathname);
      const response = await fetch(form.action + '.js', { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
      added = true;
      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(this.dataset.refresh);
      const cart = await cartResponse.json();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'cart',
        lines: [{ merchandiseId: String(data.get('id')), quantity: 1 }],
        promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: result.sections, items: cart.items, itemCount: 1, source: 'goose-cart-addons', didError: false } })
      }));
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const heading = drawer.querySelector('[data-goose-addon-heading]') || document.getElementById('cart-drawer-heading');
        if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
      }));
    } catch (failure) {
      error.textContent = added ? this.dataset.refresh : failure.message || this.dataset.error;
      error.hidden = false;
      if (added) form.dataset.refreshRequired = 'true';
    } finally {
      controls.forEach(control => { if (control.isConnected) control.disabled = false; });
      if (button.isConnected) { button.textContent = label; button.disabled = form.dataset.refreshRequired === 'true'; }
      form.removeAttribute('aria-busy');
      this.busy = false;
    }
  };
}

if (!customElements.get('goose-cart-addons')) customElements.define('goose-cart-addons', GooseCartAddons);
